import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type UsePaginationReturn,
} from '@/shared/types/pagination';
import { computeVisiblePages } from '@/shared/utils/paginationUtils';

interface UsePaginationFromUrlOptions {
  totalItems: number;
}

export function usePaginationFromUrl({
  totalItems,
}: UsePaginationFromUrlOptions): UsePaginationReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  function parsedPageSize(): number {
    const raw = Number(searchParams.get('pageSize'));
    return PAGE_SIZE_OPTIONS.includes(raw) ? raw : DEFAULT_PAGE_SIZE;
  }

  function parsedPageIndex(pages: number): number {
    const raw = Number(searchParams.get('page'));
    if (!Number.isFinite(raw) || raw < 1) return 0;
    if (pages > 0 && raw > pages) return pages - 1;
    return raw - 1; // convert 1-based URL → 0-based internal
  }

  const pageSize = parsedPageSize();

  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0;

  const pageIndex = parsedPageIndex(totalPages);

  const visiblePages = computeVisiblePages(pageIndex, totalPages);

  const canPrevious = pageIndex > 0;
  const canNext = totalPages > 0 && pageIndex < totalPages - 1;

  const setPage = (newPageIndex: number) => {
    setSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        next.set('page', String(newPageIndex + 1)); // 0-based → 1-based in URL
        return next;
      },
      { replace: true },
    );
  };

  const goToPage = (page: number) => {
    if (totalPages === 0) return;
    const clamped = Math.max(0, Math.min(page, totalPages - 1));
    setPage(clamped);
  };

  const next = () => {
    if (canNext) setPage(pageIndex + 1);
  };

  const previous = () => {
    if (canPrevious) setPage(pageIndex - 1);
  };

  const first = () => setPage(0);

  const last = () => {
    if (totalPages > 0) setPage(totalPages - 1);
  };

  const changePageSize = (size: number) => {
    setSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        next.set('pageSize', String(size));
        next.set('page', '1'); // reset to first page
        return next;
      },
      { replace: true },
    );
  };

  return {
    pageIndex,
    pageSize,
    totalItems,
    totalPages,
    visiblePages,
    canPrevious,
    canNext,
    goToPage,
    next,
    previous,
    first,
    last,
    changePageSize,
  };
}
